import type { CSSProperties, ReactElement } from 'react';
import { cn } from '../../lib/cn';

export interface SwitchProps {
  /** Whether the switch is on. */
  checked: boolean;
  /** Called with the next value when the switch is toggled (click or keyboard). */
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** CSS color for the checked track. Defaults to the theme's brand accent. */
  onColor?: string;
  id?: string;
  className?: string;
  title?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * Switch — accessible on/off toggle (`role="switch"`), theme-aware.
 *
 * Colors read `--switch-*` CSS variables with an admin dark-theme fallback
 * baked into the classes (same technique as `ImageUpload`'s `--upload-*`
 * vars); `.merchant-theme` overrides them to the light `--m-*` palette (see
 * `styles/merchant-theme.css`), so this single implementation renders
 * correctly in both the admin dark panel and the comerciante/staff light
 * panels — no second copy needed.
 *
 * Geometry is flex-based — the thumb is a normal flex child, never
 * `position: absolute`. The track is `w-9 h-5` with `p-0.5` padding forming
 * the inset, and the thumb (`h-4 w-4`) slides the exact remaining travel via
 * `translate-x-4` (36px track − 16px thumb − 2×2px padding = 16px).
 *
 * This matters: the hand-rolled switches this component replaces positioned
 * the thumb with `absolute` + `top` only (no `left`). With `left` unset, the
 * browser falls back to the CSS "static position" algorithm — which, for a
 * lone absolutely-positioned child of a `<button>` (whose UA stylesheet sets
 * `text-align: center`), does not reliably resolve to 0. A `translateX`
 * calibrated as if the rest position were 0 then lands well past the
 * track's right edge, overlapping whatever sits next to it (e.g. the
 * "Aberto" label) — confirmed by measuring the rendered boxes: the old
 * pattern overflows the track by ~13px in Chromium regardless of whether a
 * border or padding is used for the inset. Keeping the thumb in normal flex
 * flow removes the ambiguity entirely.
 */
export function Switch({
  checked,
  onChange,
  disabled = false,
  onColor,
  id,
  className,
  title,
  ...aria
}: SwitchProps): ReactElement {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      title={title}
      onClick={() => onChange(!checked)}
      style={onColor ? ({ '--switch-on': onColor } as CSSProperties) : undefined}
      className={cn(
        'inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full p-0.5',
        'transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'focus-visible:ring-[var(--switch-ring,#f97316)] focus-visible:ring-offset-[var(--switch-ring-offset,#0f172a)]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-[var(--switch-on,#f97316)]' : 'bg-[var(--switch-off,#334155)]',
        className,
      )}
      {...aria}
    >
      <span
        aria-hidden="true"
        className={cn(
          'h-4 w-4 rounded-full bg-white shadow transition-transform duration-200',
          checked ? 'translate-x-4' : 'translate-x-0',
        )}
      />
    </button>
  );
}
